import { useState } from 'react'
import { useTemplateKeywordsQuery } from '@/api/hooks/use-template-keywords'
import type { TemplateCmsKeyword } from '@/api/types/template'
import { MultiSelectChipCombobox } from '@/components/app/panels/right-panel/combo-box/multi-select-chip-combobox'

export type SearchTemplateKeywordsComboboxProps = {
  /** Selected keyword ids (multi-select chips). */
  value: string[]
  onChange: (keywordIds: string[]) => void
  /** Reports open/close so a host panel can track "is one of my dropdowns open" (e.g. for an
   *  Escape-key priority chain) without owning this component's internal state. */
  onOpenChange?: (open: boolean) => void
}

function getKeywordId(keyword: TemplateCmsKeyword): string {
  return String(keyword.cmsKeywordId)
}

function getKeywordLabel(keyword: TemplateCmsKeyword): string {
  return keyword.cmsKeywordName
}

function keywordMatchesSearch(keyword: TemplateCmsKeyword, query: string): boolean {
  return keyword.cmsKeywordName.toLowerCase().includes(query.toLowerCase())
}

/** Templates' keyword picker — same shape as `SearchBackgroundKeywordsCombobox`. */
export function SearchTemplateKeywordsCombobox({
  value,
  onChange,
  onOpenChange,
}: SearchTemplateKeywordsComboboxProps) {
  const [open, setOpen] = useState(false)

  function handleOpenChange(next: boolean) {
    setOpen(next)
    onOpenChange?.(next)
  }

  const {
    data: keywords = [],
    isLoading,
    isError,
    error,
  } = useTemplateKeywordsQuery({
    enabled: open || value.length > 0,
  })

  return (
    <MultiSelectChipCombobox
      value={value}
      onChange={onChange}
      items={keywords}
      getId={getKeywordId}
      getLabel={getKeywordLabel}
      matchesSearch={keywordMatchesSearch}
      triggerLabel="Search keywords"
      searchPlaceholder="Search keywords"
      loadingMessage="Loading keywords…"
      errorMessage={error instanceof Error ? error.message : 'Failed to load keywords'}
      emptyMessage="No matching keywords"
      chipsTestId="template-keyword-filter-chips"
      isLoading={isLoading}
      isError={isError}
      open={open}
      onOpenChange={handleOpenChange}
    />
  )
}
